import React, { useState, useEffect, useContext } from 'react'
import { Modal, Button, message } from 'antd'
import { CloseOutlined, HolderOutlined } from '@ant-design/icons'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { translate } from '../../../core'
import { AppraisalEntry, Appraisal, AppraisalLineEntry } from '../type'
import { PageContextValue } from '../../../core/type/props'
import { AppraisalPageContext } from './AppraisalPageContext'

interface AppraisalReorderModalProps {
  open: boolean
  onClose: () => void
  onComplete: (lines: AppraisalLineEntry[]) => void
  lines: AppraisalLineEntry[]
}

interface SortableItemProps {
  line: AppraisalLineEntry & { id: number }
  index: number
}

const SortableItem: React.FC<SortableItemProps> = ({ line, index }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: line.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    border: isDragging ? '2px dashed #1890ff' : '1px solid #d9d9d9',
    backgroundColor: isDragging ? '#e6f7ff' : 'transparent',
  }

  const formatCurrency = (amount: number | null | undefined) => {
    if (!amount) return '$0.00'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount)
  }

  const getItemTitle = () => {
    if (line.item?.description) {
      return line.item.description
    }
    return '-'
  }

  const getItemSubtitle = () => {
    return line.description || '-'
  }

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        height: '80px',
        minHeight: '80px',
        maxHeight: '80px',
      }}
      className="d-flex align-items-center p-3 mb-2 rounded"
      {...attributes}
    >
      <div
        {...listeners}
        className="me-3"
        style={{
          cursor: 'grab',
          color: '#8c8c8c',
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <HolderOutlined style={{ fontSize: '20px' }} />
      </div>
      <div
        className="rounded-circle d-flex align-items-center justify-content-center me-3"
        style={{
          width: '32px',
          height: '32px',
          backgroundColor: '#1890ff',
          color: 'white',
          fontWeight: 'bold',
          flexShrink: 0,
        }}
      >
        {index + 1}
      </div>
      <div 
        className="flex-grow-1"
        style={{
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <div 
          style={{ 
            fontWeight: 500, 
            marginBottom: '4px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={getItemTitle()}
        >
          {getItemTitle()}
        </div>
        {getItemSubtitle() && (
          <div 
            style={{ 
              color: '#8c8c8c', 
              fontSize: '14px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={getItemSubtitle()}
          >
            {getItemSubtitle()}
          </div>
        )}
      </div>
      <div
        style={{
          fontWeight: 'bold',
          color: '#1890ff',
          fontSize: '16px',
          flexShrink: 0,
          marginLeft: '12px',
        }}
      >
        {formatCurrency(line.amount)}
      </div>
    </div>
  )
}

export const AppraisalReorderModal: React.FC<AppraisalReorderModalProps> = ({
  open,
  onClose,
  onComplete,
  lines,
}) => {
  const [items, setItems] = useState<(AppraisalLineEntry & { id: number })[]>([])
  const [messageApi, contextHolder] = message.useMessage()

  const { refresh } = useContext<PageContextValue<Appraisal, AppraisalEntry>>(AppraisalPageContext)

    const sensors = useSensors(
      useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  useEffect(() => {
    if (open && lines.length > 0) {
      const sortedLines = [...lines]
        .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
      
      setItems(sortedLines.map((line, index) => ({
        ...line,
        id: index + 1,
      })))
    }
  }, [open, lines])


  const handleDragEnd = (event: DragEndEvent) => {
    console.log('[EVENT]', event)
    const { active, over } = event

    if (over && active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id)
        const newIndex = items.findIndex((item) => item.id === over.id)
        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const handleConfirm = () => {
    onComplete(items.map((it, index) => ({
      ...it,
      displayOrder: index + 1,
    })))
  }

  return (
    <>
      {contextHolder}
      <Modal
        title={
          <div className="d-flex align-items-center justify-content-between">
            <span>{translate('appraisal', 'reorder', 'items') || 'Reorder Appraisal Items'}</span>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={onClose}
              style={{ marginTop: '-4px' }}
            />
          </div>
        }
        open={open}
        onCancel={onClose}
        closable={false}
        footer={[
          <Button key="cancel" onClick={onClose}>
            {translate('cancel')}
          </Button>,
          <Button
            key="confirm"
            type="primary"
            onClick={handleConfirm}
          >
            {translate('confirm')}
          </Button>,
        ]}
        width={800}
        maskClosable={false}
      >
        <div className="mb-3" style={{ color: '#8c8c8c' }}>
          Drag and drop items to change their order in the final report.
        </div>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={items.map((item) => item.id)}
            strategy={verticalListSortingStrategy}
          >
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {items.map((item, index) => (
                <SortableItem key={item.id} line={item} index={index} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </Modal>
    </>
  )
}

